import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Minus, Plus, Check, Truck, Shield, RotateCcw, Star, Loader2, Heart, Zap, Camera, X, Bell } from 'lucide-react';
import { productsApi, productReviewsApi, checkoutApi } from '../lib/api';
import { trackViewItem, trackBeginCheckout } from '../lib/analytics';
import { useCart } from '../lib/cartContext';
import { useAuth } from '../lib/AuthContext';
import { useSaved } from '../lib/SavedContext';
import { useRecentlyViewedProducts } from '../lib/useRecentlyViewedProducts';
import RecentlyViewedProducts from '../components/shop/RecentlyViewedProducts';
import AlsoBoughtSection from '../components/shop/AlsoBoughtSection';
import { RedoPDPBadge } from '../components/redo/RedoCoverageToggle';
import { PointsEarnLine } from '../components/rewards/PointsEarnLine';
import { useRedoCoverage } from '../lib/useRedoCoverage';
import { sanitizeHtml } from '../lib/sanitize';
import { ProductSchema } from '../components/seo/SchemaMarkup';

function StarRating({ rating, size = 'sm' }) {
  {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
  const sizeClass = size === 'lg' ? 'w-5 h-5' : size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`${sizeClass} ${i <= rating ? 'text-[#E63946] fill-[#E63946]' : 'text-zinc-700'}`}
        />
      ))}
    </div>
  );
}

function ReviewForm({ productId, onSubmitted }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ rating: 5, title: '', review_text: '' });
  const [guest, setGuest] = useState({ reviewer_name: '', reviewer_email: '' });
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (success) {
    return (
      <div className="border border-green-800/30 bg-green-900/10 p-6 text-center" data-testid="review-submitted">
        <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
        <p className="text-white text-sm font-medium">Review Submitted!</p>
        <p className="text-zinc-500 text-xs mt-1">Your review will appear once approved by an admin.</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.review_text.trim()) { setError('Please write your review'); return; }
    if (!user && (!guest.reviewer_name.trim() || !guest.reviewer_email.includes('@'))) {
      setError('Please add your name and a valid email');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await productReviewsApi.submitReview(productId, { ...form, photo_url: photoUrl, ...(user ? {} : guest) });
      setSuccess(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoUploading(true);
    setError('');
    try {
      const res = await productReviewsApi.uploadPhoto(f);
      setPhotoUrl(res.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-zinc-800 bg-zinc-900/40 p-6 space-y-4" data-testid="review-form">
      <h4 className="text-white text-sm font-medium uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Write a Review</h4>
      {!user && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-zinc-500 text-xs block mb-1">Name *</label>
            <input
              type="text" value={guest.reviewer_name} onChange={e => setGuest(p => ({ ...p, reviewer_name: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-[#E63946] focus:outline-none"
              placeholder="Your name" data-testid="review-guest-name"
            />
          </div>
          <div>
            <label className="text-zinc-500 text-xs block mb-1">Email *</label>
            <input
              type="email" value={guest.reviewer_email} onChange={e => setGuest(p => ({ ...p, reviewer_email: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-[#E63946] focus:outline-none"
              placeholder="you@email.com — never shown publicly" data-testid="review-guest-email"
            />
          </div>
        </div>
      )}
      <div>
        <label className="text-zinc-500 text-xs block mb-2">Rating</label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(i => (
            <button key={i} type="button" onClick={() => setForm(p => ({ ...p, rating: i }))} data-testid={`review-star-${i}`}>
              <Star className={`w-6 h-6 transition-colors cursor-pointer ${i <= form.rating ? 'text-[#E63946] fill-[#E63946]' : 'text-zinc-700 hover:text-zinc-500'}`} />
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-zinc-500 text-xs block mb-1">Title (optional)</label>
        <input
          type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-[#E63946] focus:outline-none"
          placeholder="Sum it up in a few words" data-testid="review-title"
        />
      </div>
      <div>
        <label className="text-zinc-500 text-xs block mb-1">Review *</label>
        <textarea
          value={form.review_text} onChange={e => setForm(p => ({ ...p, review_text: e.target.value }))}
          className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-[#E63946] focus:outline-none min-h-[100px] resize-none"
          placeholder="What did you think of this product?" data-testid="review-text"
        />
      </div>
      <div>
        <label className="text-zinc-500 text-xs block mb-1">Photo (optional)</label>
        {photoUrl ? (
          <div className="relative inline-block" data-testid="review-photo-preview">
            <img src={photoUrl} alt="Your review upload" className="h-24 w-24 object-cover border border-zinc-700" />
            <button
              type="button"
              onClick={() => setPhotoUrl('')}
              className="absolute -top-2 -right-2 bg-zinc-800 hover:bg-[#E63946] text-white rounded-full p-1 transition-colors"
              data-testid="review-photo-remove"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-4 py-2.5 border border-dashed border-zinc-700 hover:border-[#E63946] text-zinc-400 text-xs cursor-pointer transition-colors" data-testid="review-photo-label">
            {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {photoUploading ? 'Uploading…' : 'Add a photo of you in your gear'}
            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} disabled={photoUploading} data-testid="review-photo-input" />
          </label>
        )}
      </div>
      {error && <p className="text-red-400 text-xs" data-testid="review-error">{error}</p>}
      <button type="submit" disabled={submitting} className="btn-ninja px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="submit-review-btn">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit Review'}
      </button>
    </form>
  );
}

function ReviewHighlight({ reviews }) {
  if (!reviews?.length) return null;
  const best = [...reviews].sort((a, b) =>
    (b.rating - a.rating) ||
    ((b.is_verified_purchase ? 1 : 0) - (a.is_verified_purchase ? 1 : 0)) ||
    ((b.review_text?.length || 0) - (a.review_text?.length || 0))
  )[0];
  if (!best || best.rating < 4 || !best.review_text) return null;
  const quote = best.review_text.length > 160 ? `${best.review_text.slice(0, 157).trimEnd()}…` : best.review_text;
  const photos = reviews.filter((r) => r.photo_url && r.rating >= 4).sort((a, b) => b.rating - a.rating).slice(0, 3);
  return (
    <a href="#reviews" className="block border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 transition-colors p-4" data-testid="review-highlight">
      <div className="flex items-center gap-2 mb-1.5">
        <StarRating rating={best.rating} />
        {best.is_verified_purchase && (
          <span className="text-green-500 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <Check className="w-3 h-3" /> Verified Purchase
          </span>
        )}
      </div>
      <p className="text-zinc-300 text-sm italic leading-relaxed" data-testid="review-highlight-quote">“{quote}”</p>
      <p className="text-zinc-600 text-xs mt-1.5" data-testid="review-highlight-name">— {best.user_name}</p>
      {photos.length > 0 && (
        <div className="flex items-center gap-2 mt-3" data-testid="review-highlight-photos">
          {photos.map((r) => (
            <img
              key={r.review_id}
              src={r.photo_url}
              alt={`${r.user_name} wearing this gear`}
              loading="lazy"
              className="h-14 w-14 object-cover border border-zinc-800"
              data-testid={`review-highlight-photo-${r.review_id}`}
            />
          ))}
          <span className="text-zinc-600 text-[10px] uppercase tracking-wider ml-1">Real athletes, real gear</span>
        </div>
      )}
    </a>
  );
}

function ReviewsList({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await productReviewsApi.getReviews(productId);
      setReviews(res.data.reviews || []);
      setStats(res.data.stats || null);
    } catch (e) { console.error('Failed to load product reviews:', e); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  if (loading) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 text-zinc-500 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="product-reviews-section">
      {/* Stats Header */}
      {stats && stats.total > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8 pb-8 border-b border-zinc-800">
          <div className="text-center sm:text-left">
            <div className="text-5xl text-white font-semibold" data-testid="review-avg-rating">{stats.average_rating}</div>
            <StarRating rating={Math.round(stats.average_rating)} size="md" />
            <p className="text-zinc-500 text-xs mt-1">{stats.total} review{stats.total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex-1 space-y-1.5 w-full max-w-xs">
            {[5, 4, 3, 2, 1].map(s => {
              const count = parseInt(stats.distribution?.[String(s)] || 0);
              const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={s} className="flex items-center gap-2 text-sm">
                  <span className="text-zinc-500 w-3">{s}</span>
                  <Star className="w-3 h-3 text-zinc-600" />
                  <div className="flex-1 h-2 bg-zinc-800 overflow-hidden">
                    <div className="h-full bg-[#E63946] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-zinc-600 text-xs w-6 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reviews */}
      {reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((r) => (
            <div key={r.review_id} className="pb-6 border-b border-zinc-800/50" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center gap-3 mb-2">
                <StarRating rating={r.rating} />
                {r.is_verified_purchase && (
                  <span className="text-green-500 text-[10px] uppercase tracking-wider flex items-center gap-1">
                    <Check className="w-3 h-3" /> Verified Purchase
                  </span>
                )}
              </div>
              {r.title && <h4 className="text-white text-sm font-medium mb-1">{r.title}</h4>}
              <p className="text-zinc-400 text-sm leading-relaxed">{r.review_text}</p>
              {r.photo_url && (
                <a href={r.photo_url} target="_blank" rel="noopener noreferrer" data-testid={`review-photo-${r.review_id}`}>
                  <img
                    src={r.photo_url}
                    alt={`${r.user_name}'s photo`}
                    loading="lazy"
                    className="mt-3 h-24 w-24 object-cover border border-zinc-800 hover:opacity-80 transition-opacity"
                  />
                </a>
              )}
              <div className="flex items-center gap-2 mt-3">
                <span className="text-zinc-600 text-xs">{r.user_name}</span>
                <span className="text-zinc-700">|</span>
                <span className="text-zinc-700 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-zinc-500 text-sm py-4" data-testid="no-reviews">No reviews yet. Be the first to share your experience!</p>
      )}

      {/* Review Form */}
      <div className="mt-8">
        <ReviewForm productId={productId} onSubmitted={fetchReviews} />
      </div>
    </div>
  );
}

const isVariantSoldOut = (v) => v?.available === false || (typeof v?.inventory_quantity === 'number' && v.inventory_quantity <= 0);

const NotifyMeForm = ({ productSlug, size }) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | busy | done | already | error
  const [msg, setMsg] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setState('busy');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock-alerts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), product_slug: productSlug, size: size || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.[0]?.msg || d.detail || 'Something went wrong');
      setState(d.status === 'already_registered' ? 'already' : 'done');
    } catch (err) { setMsg(err.message); setState('error'); }
  };
  if (state === 'done' || state === 'already') {
    return (
      <p className="flex items-center gap-2 text-[#C7FA3E] text-sm border border-[#C7FA3E]/30 bg-[#C7FA3E]/[0.05] px-4 py-3.5" data-testid="stock-notify-success">
        <Check className="w-4 h-4 flex-shrink-0" />
        {state === 'already' ? "You're already on the list — we'll email you the moment it's back." : "You're on the list. We'll email you the moment it's back in stock."}
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="border border-zinc-700 bg-zinc-900/60 p-4" data-testid="stock-notify-form">
      <p className="text-white text-sm font-bold uppercase tracking-wider mb-1">Sold out{size ? ` in ${size}` : ''}</p>
      <p className="text-zinc-500 text-xs mb-3">Drop your email and we&apos;ll ping you the moment it&apos;s restocked.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
          className="flex-1 bg-black border border-zinc-700 text-white text-sm px-3 py-3 focus:border-[#C7FA3E] outline-none"
          data-testid="stock-notify-email" />
        <button type="submit" disabled={state === 'busy' || !email.trim()}
          className="bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest px-6 py-3 disabled:opacity-40 hover:bg-[#d4ff5c] transition-colors flex items-center justify-center gap-2"
          data-testid="stock-notify-btn">
          <Bell className="w-3.5 h-3.5" /> {state === 'busy' ? 'Saving…' : 'Notify me'}
        </button>
      </div>
      {state === 'error' && <p className="text-red-400 text-xs mt-2" data-testid="stock-notify-error">{msg}</p>}
    </form>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem, loading: cartLoading } = useCart();
  const { user } = useAuth();
  const { isSaved, toggleSaved } = useSaved();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [buyNowLoading, setBuyNowLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const { trackView } = useRecentlyViewedProducts();

  const redoCoverage = useRedoCoverage(
    product ? [{ product_id: product.id, shopify_variant_id: product.variants?.[0]?.id, price: product.price, quantity }] : [],
    product ? product.price * quantity : 0,
  );

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productsApi.getBySlug(slug);
        setProduct(response.data);
        const vs = response.data?.variants || [];
        if (vs.length) {
          setSelectedVariant(vs.find((v) => !isVariantSoldOut(v)) || vs[0]);
        }
        trackView(response.data);
        trackViewItem(response.data);
        // Fetch review stats
        if (response.data?.id) {
          try {
            const revRes = await productReviewsApi.getReviews(response.data.id);
            setReviewStats(revRes.data.stats);
            setApprovedReviews(revRes.data.reviews || []);
          } catch (e) { console.error('Failed to load review stats:', e); }
        }
      } catch (error) {
        console.error('Product fetch failed:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, trackView]);

  const handleAddToCart = async () => {
    setIsAdding(true);
    await addItem(product.id, quantity, selectedVariant ? { id: selectedVariant.id, title: selectedVariant.title } : null);
    setIsAdding(false);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = async () => {
    setBuyNowLoading(true);
    trackBeginCheckout((product.price || 0) * quantity, [{
      item_id: product.id,
      item_name: product.title || product.name,
      item_variant: selectedVariant?.title || '',
      price: product.price,
      quantity,
    }], 'buy_now');
    try {
      const { data } = await checkoutApi.shopifyBuyNowUrl({
        product_slug: product.slug,
        variant_id: selectedVariant?.id ? String(selectedVariant.id) : null,
        quantity,
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Buy Now error:', err);
      const msg = err?.response?.data?.detail || 'Unable to start checkout. Please try again.';
      alert(msg);
    } finally {
      setBuyNowLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing">
          <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-zinc-800" />
            <div><div className="h-8 bg-zinc-800 w-1/4 mb-4" /><div className="h-12 bg-zinc-800 w-3/4 mb-4" /><div className="h-24 bg-zinc-800 mb-8" /></div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing text-center">
          <h1 className="text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Product Not Found</h1>
          <Link to="/shop" className="btn-ninja inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to Shop</Link>
        </div>
      </main>
    );
  }

  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  const images = product.images?.length > 0 ? product.images : ['https://placehold.co/800x800/0a0a0a/ffffff?text=Product'];

  return (
    <main className="pt-20 min-h-screen" data-testid="product-page">
      <ProductSchema product={product} reviewStats={reviewStats} reviews={approvedReviews} />
      <div className="container-ninja section-spacing">
        <Link to="/shop" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors duration-300 mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Shop</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="aspect-square overflow-hidden bg-zinc-900 mb-4">
              <img src={images[selectedImage]} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-4">
                {images.map((image, index) => (
                  <button key={`${image}-${index}`} onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 overflow-hidden border-2 transition-colors duration-300 ${selectedImage === index ? 'border-[#E63946]' : 'border-transparent'}`}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <span className="text-xs text-zinc-500 uppercase tracking-wider">{product.category}</span>
            <h1 className="text-4xl md:text-5xl text-white mt-2 mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{product.name}</h1>

            {/* Star Rating Summary */}
            {reviewStats && reviewStats.total > 0 && (
              <div className="flex items-center gap-2 mb-4" data-testid="product-rating-summary">
                <StarRating rating={Math.round(reviewStats.average_rating)} size="sm" />
                <span className="text-zinc-400 text-sm">{reviewStats.average_rating}</span>
                <a href="#reviews" className="text-zinc-500 text-sm hover:text-zinc-300 transition-colors">
                  ({reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''})
                </a>
              </div>
            )}

            <p className="text-zinc-400 text-lg mb-6">{product.description}</p>

            {/* Price */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-3xl text-[#E63946] font-semibold">${(selectedVariant?.price ?? product.price).toFixed(2)}</span>
              {hasDiscount && (
                <>
                  <span className="text-xl text-zinc-600 line-through">${product.compare_at_price.toFixed(2)}</span>
                  <span className="px-2 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white">Save ${(product.compare_at_price - product.price).toFixed(2)}</span>
                </>
              )}
            </div>

            {/* Variant / Size selector */}
            {(product.variants || []).filter((v) => v.title && v.title !== 'Default Title').length > 0 && (
              <div className="mb-8" data-testid="variant-selector">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">
                  {product.variants.some((v) => (v.option1 || '').match(/^(XS|S|M|L|XL|2XL|XXL|3XL)/i)) ? 'Size' : 'Option'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => {
                    const active = selectedVariant === v;
                    const soldOut = isVariantSoldOut(v);
                    return (
                      <button
                        key={v.id || v.title}
                        onClick={() => setSelectedVariant(v)}
                        className={`px-4 py-2 text-sm border transition-all duration-300 ${
                          active
                            ? 'border-[#E63946] bg-[#E63946]/10 text-white'
                            : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'
                        } ${soldOut ? 'opacity-40 line-through' : ''}`}
                        data-testid={`variant-option-${v.id || v.title}`}
                        title={soldOut ? 'Sold out — tap to get notified' : v.title}
                      >
                        {v.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity & Add to Cart / Notify me when sold out */}
            {(() => {
              const sizedVariants = (product.variants || []).filter((v) => v.title && v.title !== 'Default Title');
              const selectedSoldOut = sizedVariants.length > 0
                ? isVariantSoldOut(selectedVariant)
                : (typeof product.inventory_count === 'number' && product.inventory_count <= 0);
              if (selectedSoldOut) {
                return (
                  <div className="flex flex-col gap-4 mb-8">
                    <NotifyMeForm key={selectedVariant?.title || 'product'} productSlug={product.slug} size={sizedVariants.length > 0 ? selectedVariant?.title : null} />
                  </div>
                );
              }
              return (
            <div className="flex flex-col gap-4 mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center border border-zinc-700">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-12 h-12 flex items-center justify-center text-zinc-400 hover:text-white transition-colors" data-testid="decrease-quantity">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center text-white">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-12 h-12 flex items-center justify-center text-zinc-400 hover:text-white transition-colors" data-testid="increase-quantity">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button onClick={handleAddToCart} disabled={isAdding || cartLoading}
                  className={`flex-1 py-4 px-8 uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-all duration-300 ${added ? 'bg-green-600 text-white' : 'btn-ninja'}`}
                  data-testid="product-add-to-cart"
                >
                  {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                  {added ? (<><Check className="w-5 h-5" /> Added to Cart</>) : isAdding ? 'Adding...' : (<><ShoppingBag className="w-5 h-5" /> Add to Cart</>)}
                </button>
                <button
                  onClick={() => {
                    if (!user) { window.location.href = '/auth'; return; }
                    toggleSaved({
                      type: 'product',
                      item_id: product.id,
                      title: product.name,
                      image: product.images?.[0] || '',
                      category: product.category || '',
                      price: product.price,
                      slug: product.slug,
                    });
                  }}
                  className={`w-14 h-14 flex items-center justify-center border transition-all duration-300 ${
                    isSaved(product.id, 'product')
                      ? 'border-[#E63946] bg-[#E63946]/10 text-[#E63946]'
                      : 'border-zinc-700 text-zinc-400 hover:border-[#E63946]/40 hover:text-[#E63946]'
                  }`}
                  data-testid="product-wishlist-btn"
                  aria-label={isSaved(product.id, 'product') ? 'Remove from wishlist' : 'Add to wishlist'}
                >
                  <Heart className={`w-5 h-5 ${isSaved(product.id, 'product') ? 'fill-[#E63946]' : ''}`} />
                </button>
              </div>
              {product.active !== false && (
                <button
                  onClick={handleBuyNow}
                  disabled={buyNowLoading}
                  className="w-full py-4 px-8 uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-all duration-300 bg-[#E63946] hover:bg-[#c62d39] text-white disabled:opacity-50"
                  data-testid="product-buy-now"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {buyNowLoading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</>) : (<><Zap className="w-5 h-5" /> Buy Now</>)}
                </button>
              )}
            </div>
              );
            })()}
            <ReviewHighlight reviews={approvedReviews} />

            {/* Points earn incentive */}
            <PointsEarnLine
              amount={(selectedVariant?.price ?? product.price) * quantity}
              className="mb-4"
              testId="product-points-earn"
            />

            {/* Redo Free Returns Badge */}
            <RedoPDPBadge coverage={redoCoverage} />

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 py-6 border-t border-b border-zinc-800">
              {[
                { icon: Truck, label: 'Free Shipping' },
                { icon: Shield, label: 'Secure Checkout' },
                { icon: RotateCcw, label: '30-Day Returns' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="text-center">
                  <Icon className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
                  <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
                </div>
              ))}
            </div>

            {/* Specs */}
            {product.specs && product.specs.length > 0 && (
              <div className="mt-8">
                <h3 className="text-white text-xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Specifications</h3>
                <div className="space-y-2">
                  {product.specs.map((spec) => (
                    <div key={spec.label} className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-500">{spec.label}</span>
                      <span className="text-white">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Details */}
        {product.long_description && (
          <div className="mt-16 max-w-3xl">
            <h2 className="text-2xl text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Product Details</h2>
            <div className="prose prose-invert prose-lg max-w-none prose-headings:font-normal prose-headings:tracking-wide prose-h3:text-xl prose-h3:text-zinc-200 prose-h3:mt-8 prose-h3:mb-4 prose-p:text-zinc-400 prose-p:leading-relaxed prose-strong:text-white prose-li:text-zinc-400"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.long_description) }}
            />
          </div>
        )}

        {/* Reviews Section */}
        {product?.id && (
          <div id="reviews" className="mt-16 pt-8 border-t border-zinc-800">
            <h2 className="text-2xl text-white mb-8" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Customer Reviews</h2>
            <ReviewsList productId={product.id} />
          </div>
        )}
      </div>

      {/* Customers Also Bought */}
      {product?.id && <AlsoBoughtSection productId={product.id} />}

      {/* Recently Viewed Products */}
      {product?.id && <RecentlyViewedProducts excludeId={product.id} />}
    </main>
  );
}
